import MetaAgentAPI from 'dashboard/api/metaAgent/agent';

// The Studio reads Meta live rather than mirroring it, so this module holds the
// current inbox's remote state instead of a record collection.
export const state = {
  inboxes: [],
  agent: {},
  settings: {},
  allowlist: [],
  eligibility: {},
  businessInfo: {},
  faqs: [],
  websites: [],
  files: [],
  skills: [],
  uiSkills: [],
  uiFlags: {
    fetchingInboxes: false,
    fetchingAgent: false,
    updatingSettings: false,
    checkingEligibility: false,
    onboarding: false,
    fetchingKnowledge: false,
    savingKnowledge: false,
    fetchingSkills: false,
    savingSkill: false,
    sendingPlaygroundMessage: false,
  },
};

export const getters = {
  getInboxes: $state => $state.inboxes,
  getInbox: $state => inboxId =>
    $state.inboxes.find(inbox => inbox.id === Number(inboxId)) || {},
  getAgent: $state => $state.agent,
  getSettings: $state => $state.settings,
  getAllowlist: $state => $state.allowlist,
  getEligibility: $state => $state.eligibility,
  getBusinessInfo: $state => $state.businessInfo,
  getFaqs: $state => $state.faqs,
  getWebsites: $state => $state.websites,
  getFiles: $state => $state.files,
  getSkills: $state => $state.skills,
  getUiSkills: $state => $state.uiSkills,
  getUIFlags: $state => $state.uiFlags,
};

export const mutations = {
  SET_UI_FLAG($state, flags) {
    $state.uiFlags = { ...$state.uiFlags, ...flags };
  },
  SET_INBOXES($state, inboxes) {
    $state.inboxes = inboxes;
  },
  SET_AGENT($state, agent) {
    $state.agent = agent || {};
    $state.settings = agent?.settings || {};
  },
  SET_ALLOWLIST($state, entries) {
    $state.allowlist = entries;
  },
  SET_ELIGIBILITY($state, { inboxId, isEligible }) {
    $state.eligibility = { ...$state.eligibility, [inboxId]: isEligible };
  },
  SET_BUSINESS_INFO($state, businessInfo) {
    $state.businessInfo = businessInfo || {};
  },
  SET_FAQS($state, faqs) {
    $state.faqs = faqs;
  },
  SET_WEBSITES($state, websites) {
    $state.websites = websites;
  },
  SET_FILES($state, files) {
    $state.files = files;
  },
  SET_SKILLS($state, skills) {
    $state.skills = skills;
  },
  SET_UI_SKILLS($state, uiSkills) {
    $state.uiSkills = uiSkills;
  },
  CLEAR_AGENT($state) {
    $state.agent = {};
    $state.settings = {};
    $state.allowlist = [];
    $state.businessInfo = {};
    $state.faqs = [];
    $state.websites = [];
    $state.files = [];
    $state.skills = [];
    $state.uiSkills = [];
  },
};

export const actions = {
  async getInboxes({ commit }) {
    commit('SET_UI_FLAG', { fetchingInboxes: true });
    try {
      const { data } = await MetaAgentAPI.getInboxes();
      commit('SET_INBOXES', data.payload);
    } finally {
      commit('SET_UI_FLAG', { fetchingInboxes: false });
    }
  },

  async getAgent({ commit }, inboxId) {
    commit('SET_UI_FLAG', { fetchingAgent: true });
    try {
      const { data } = await MetaAgentAPI.getAgent(inboxId);
      commit('SET_AGENT', data);
    } catch (error) {
      // A 404 simply means this inbox has not started onboarding yet.
      commit('CLEAR_AGENT');
    } finally {
      commit('SET_UI_FLAG', { fetchingAgent: false });
    }
  },

  async checkEligibility({ commit }, inboxId) {
    commit('SET_UI_FLAG', { checkingEligibility: true });
    try {
      const { data } = await MetaAgentAPI.checkEligibility(inboxId);
      commit('SET_ELIGIBILITY', { inboxId, isEligible: data.is_eligible });
      return data.is_eligible;
    } finally {
      commit('SET_UI_FLAG', { checkingEligibility: false });
    }
  },

  async attestTerms({ commit }, inboxId) {
    const { data } = await MetaAgentAPI.attestTerms(inboxId);
    commit('SET_AGENT', data);
    return data;
  },

  async onboard({ commit, dispatch }, inboxId) {
    commit('SET_UI_FLAG', { onboarding: true });
    try {
      const { data } = await MetaAgentAPI.onboard(inboxId);
      commit('SET_AGENT', data);
      dispatch('getInboxes');
      return data;
    } finally {
      commit('SET_UI_FLAG', { onboarding: false });
    }
  },

  async updateSettings({ commit, dispatch }, { inboxId, settings, confirm }) {
    commit('SET_UI_FLAG', { updatingSettings: true });
    try {
      const { data } = await MetaAgentAPI.updateSettings(inboxId, {
        settings,
        confirm,
      });
      commit('SET_AGENT', data);
      dispatch('getInboxes');
      return data;
    } finally {
      commit('SET_UI_FLAG', { updatingSettings: false });
    }
  },

  async deleteAgent({ commit, dispatch }, inboxId) {
    await MetaAgentAPI.deleteAgent(inboxId);
    commit('CLEAR_AGENT');
    dispatch('getInboxes');
  },

  async getAllowlist({ commit }, inboxId) {
    const { data } = await MetaAgentAPI.getAllowlist(inboxId);
    commit('SET_ALLOWLIST', data.payload);
  },

  async addAllowlistEntry({ dispatch }, { inboxId, consumerPhoneNumber }) {
    await MetaAgentAPI.addAllowlistEntry(inboxId, consumerPhoneNumber);
    dispatch('getAllowlist', inboxId);
  },

  async removeAllowlistEntry({ dispatch }, { inboxId, entryId }) {
    await MetaAgentAPI.removeAllowlistEntry(inboxId, entryId);
    dispatch('getAllowlist', inboxId);
  },

  // The four knowledge sources are independent calls to Meta. allSettled rather
  // than all: one failing resource must not blank the other three tabs. The
  // first rejection is still re-thrown so the page can report it.
  async getKnowledge({ commit }, inboxId) {
    commit('SET_UI_FLAG', { fetchingKnowledge: true });
    const sources = [
      {
        mutation: 'SET_BUSINESS_INFO',
        request: MetaAgentAPI.getBusinessInfo(inboxId),
        envelope: false,
      },
      {
        mutation: 'SET_FAQS',
        request: MetaAgentAPI.getFaqs(inboxId),
        envelope: true,
      },
      {
        mutation: 'SET_WEBSITES',
        request: MetaAgentAPI.getWebsites(inboxId),
        envelope: true,
      },
      {
        mutation: 'SET_FILES',
        request: MetaAgentAPI.getFiles(inboxId),
        envelope: true,
      },
    ];

    try {
      const results = await Promise.allSettled(
        sources.map(source => source.request)
      );
      results.forEach((result, index) => {
        if (result.status !== 'fulfilled') return;
        const { mutation, envelope } = sources[index];
        commit(
          mutation,
          envelope ? result.value.data.payload : result.value.data
        );
      });

      const failure = results.find(result => result.status === 'rejected');
      if (failure) throw failure.reason;
    } finally {
      commit('SET_UI_FLAG', { fetchingKnowledge: false });
    }
  },

  async updateBusinessInfo({ commit }, { inboxId, businessInfo }) {
    commit('SET_UI_FLAG', { savingKnowledge: true });
    try {
      const { data } = await MetaAgentAPI.updateBusinessInfo(
        inboxId,
        businessInfo
      );
      commit('SET_BUSINESS_INFO', data);
      return data;
    } finally {
      commit('SET_UI_FLAG', { savingKnowledge: false });
    }
  },

  async getFaqs({ commit }, inboxId) {
    const { data } = await MetaAgentAPI.getFaqs(inboxId);
    commit('SET_FAQS', data.payload);
  },

  async saveFaq({ dispatch }, { inboxId, faqId, faq }) {
    if (faqId) {
      await MetaAgentAPI.updateFaq(inboxId, faqId, faq);
    } else {
      await MetaAgentAPI.createFaq(inboxId, faq);
    }
    dispatch('getFaqs', inboxId);
  },

  async deleteFaq({ dispatch }, { inboxId, faqId }) {
    await MetaAgentAPI.deleteFaq(inboxId, faqId);
    dispatch('getFaqs', inboxId);
  },

  async getWebsites({ commit }, inboxId) {
    const { data } = await MetaAgentAPI.getWebsites(inboxId);
    commit('SET_WEBSITES', data.payload);
  },

  async addWebsite({ dispatch }, { inboxId, url }) {
    await MetaAgentAPI.createWebsite(inboxId, url);
    dispatch('getWebsites', inboxId);
  },

  async deleteWebsite({ dispatch }, { inboxId, websiteId }) {
    await MetaAgentAPI.deleteWebsite(inboxId, websiteId);
    dispatch('getWebsites', inboxId);
  },

  async getFiles({ commit }, inboxId) {
    const { data } = await MetaAgentAPI.getFiles(inboxId);
    commit('SET_FILES', data.payload);
  },

  async uploadFile({ commit, dispatch }, { inboxId, file }) {
    commit('SET_UI_FLAG', { savingKnowledge: true });
    try {
      await MetaAgentAPI.uploadFile(inboxId, file);
      dispatch('getFiles', inboxId);
    } finally {
      commit('SET_UI_FLAG', { savingKnowledge: false });
    }
  },

  async deleteFile({ dispatch }, { inboxId, fileId }) {
    await MetaAgentAPI.deleteFile(inboxId, fileId);
    dispatch('getFiles', inboxId);
  },

  async getAllSkills({ commit }, inboxId) {
    commit('SET_UI_FLAG', { fetchingSkills: true });
    try {
      const [skills, uiSkills] = await Promise.all([
        MetaAgentAPI.getSkills(inboxId),
        MetaAgentAPI.getUiSkills(inboxId),
      ]);
      commit('SET_SKILLS', skills.data.payload);
      commit('SET_UI_SKILLS', uiSkills.data.payload);
    } finally {
      commit('SET_UI_FLAG', { fetchingSkills: false });
    }
  },

  async saveSkill({ commit, dispatch }, { inboxId, skillId, skill }) {
    commit('SET_UI_FLAG', { savingSkill: true });
    try {
      if (skillId) {
        await MetaAgentAPI.updateSkill(inboxId, skillId, skill);
      } else {
        await MetaAgentAPI.createSkill(inboxId, skill);
      }
      dispatch('getAllSkills', inboxId);
    } finally {
      commit('SET_UI_FLAG', { savingSkill: false });
    }
  },

  async deleteSkill({ dispatch }, { inboxId, skillId }) {
    await MetaAgentAPI.deleteSkill(inboxId, skillId);
    dispatch('getAllSkills', inboxId);
  },

  async saveUiSkill({ commit, dispatch }, { inboxId, uiSkillId, uiSkill }) {
    commit('SET_UI_FLAG', { savingSkill: true });
    try {
      if (uiSkillId) {
        await MetaAgentAPI.updateUiSkill(inboxId, uiSkillId, uiSkill);
      } else {
        await MetaAgentAPI.createUiSkill(inboxId, uiSkill);
      }
      dispatch('getAllSkills', inboxId);
    } finally {
      commit('SET_UI_FLAG', { savingSkill: false });
    }
  },

  async deleteUiSkill({ dispatch }, { inboxId, uiSkillId }) {
    await MetaAgentAPI.deleteUiSkill(inboxId, uiSkillId);
    dispatch('getAllSkills', inboxId);
  },

  // The playground thread is local to the open page: nothing is persisted at
  // Meta beyond the conversation_id that links the turns.
  async sendPlaygroundMessage(
    { commit },
    { inboxId, userMsg, conversationId }
  ) {
    commit('SET_UI_FLAG', { sendingPlaygroundMessage: true });
    try {
      const { data } = await MetaAgentAPI.sendPlaygroundMessage(inboxId, {
        userMsg,
        conversationId,
      });
      return data;
    } finally {
      commit('SET_UI_FLAG', { sendingPlaygroundMessage: false });
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
